import React, { useState } from 'react';
import { AuthenticatedTemplate } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';
import { ExpandLess, ExpandMore, Description as AppealLetterIcon } from '@mui/icons-material';
import { Collapse, List, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import { useAppSelector } from '../../../../../app/hooks';
import { useGetMyProfileQuery } from '../../../../../services/ProfileService';
import { hasAppealsAccess } from '../../../../profile/utils/profileUtils';

// Types
interface AppealsMenuItemConfig {
    id: string;
    title: string;
    icon: React.ComponentType<{ sx?: any }>;
    route: string;
}

// Constants
const MENU_STYLES = {
    navList: { 
        border: 'black solid 1px' 
    },
    nestedListItem: { 
        marginTop: '0px',
        paddingLeft: '48px'
    },
    icon: { 
        color: 'white' 
    }
} as const;

const APPEALS_MENU_ITEMS: AppealsMenuItemConfig[] = [
    {
        id: 'appeal-letters',
        title: 'Appeal Letters',
        icon: AppealLetterIcon,
        route: '/appeals'
    }
];

// Sub-components
interface AppealsMenuItemProps {
    item: AppealsMenuItemConfig;
    onClick: () => void;
}

const AppealsMenuItem: React.FC<AppealsMenuItemProps> = ({ item, onClick }) => {
    const { title, icon: Icon } = item;

    return (
        <ListItemButton 
            sx={MENU_STYLES.nestedListItem} 
            onClick={onClick}
        >
            <ListItemIcon>
                <Icon sx={MENU_STYLES.icon} />
            </ListItemIcon>
            <ListItemText primary={title} />
        </ListItemButton>
    );
};

// Main component
export const AppealsMenu: React.FC = () => {
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    
    const selectedCenter = useAppSelector(state => state.center.selectedCenter);
    const { data: profile } = useGetMyProfileQuery();
    const company = profile?.organization || '';

    const handleItemClick = (item: AppealsMenuItemConfig): void => {
        const params = new URLSearchParams();
        if (company) params.append('company', company);
        if (selectedCenter) params.append('center', selectedCenter);
        
        const routeWithParams = `${item.route}?${params.toString()}`;
        navigate(routeWithParams);
    };

    const toggleMenu = (): void => setOpen(!open);

    if (!hasAppealsAccess(profile)) {
        return null;
    }

    return (
        <AuthenticatedTemplate>
            <List component="nav" sx={MENU_STYLES.navList}>
                <ListItemButton onClick={toggleMenu}>
                    <ListItemText primary="Appeals" />
                    {open ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
                <Collapse in={open} timeout="auto" unmountOnExit>
                    {APPEALS_MENU_ITEMS.map((item) => (
                        <AppealsMenuItem
                            key={item.id}
                            item={item}
                            onClick={() => handleItemClick(item)}
                        />
                    ))}
                </Collapse>
            </List>
        </AuthenticatedTemplate>
    );
};

export default AppealsMenu;
